import AppContainer from "@/components/shared/AppContainer";
import tw from "twrnc";

import React, { useEffect, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import AppHeader from "@/components/shared/AppHeader";
import CreateEventForm from "@/components/shared/CreateEventForm";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { colors, CONTENT_MAX_WIDTH } from "@/components/settings/theme";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import {
  fundraiserEventTitle,
  peekFundraiserIntent,
  readFundraiserIntent,
} from "@/components/donations/fundraiserIntent";
import {
  ensureNonprofitWaiverClaim,
  nonprofitWaiverMessage,
} from "@/components/donations/nonprofitWaiver";
import { t, tn } from "@/localization";

const isTruthyParam = (value) =>
  value === true || value === "true" || value === "1";

const CreateEvent = ({ navigation, route }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const form = useRef();
  const { first, fundraiser, funeral } = route.params || {};
  const fundraiserMode = DONATIONS_ENABLED && isTruthyParam(fundraiser);
  const [intent, setIntent] = useState(() =>
    fundraiserMode ? peekFundraiserIntent() : null
  );

  useEffect(() => {
    if (!fundraiserMode) return;
    let active = true;
    readFundraiserIntent().then((value) => active && setIntent(value));
    return () => {
      active = false;
    };
  }, [fundraiserMode]);

  // Teams: events are created in the account you work in (its plan and
  // account type; members don't see its credits).
  const {
    broadcaster,
    credits,
    account_type,
    broadcaster_role,
    refetchProfile,
  } = useWorkspaceAuth();
  // Funeral homes schedule services (from the dashboard welcome, or any
  // funeral home account).
  const funeralMode =
    !fundraiserMode &&
    (isTruthyParam(funeral) || broadcaster_role === "funeralhome");

  // Nonprofits from eventlive.pro/nonprofit: evt.live's answer to the waiver
  // claim (made here if EventList hasn't yet; saving waits for it too).
  const [waiverClaim, setWaiverClaim] = useState(null);
  useEffect(() => {
    let active = true;
    ensureNonprofitWaiverClaim().then((claim) => {
      if (!active) return;
      setWaiverClaim(claim);
      // The account became business/nonprofit on evt.live.
      if (claim?.status === "granted" && account_type !== "business") {
        refetchProfile();
      }
    });
    return () => {
      active = false;
    };
  }, []);
  const waiverMessage = nonprofitWaiverMessage(waiverClaim);

  const onBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("EventList");
    }
  };

  const creditsText = funeralMode
    ? tn(
        "You have {count} event credit. Each service day uses one.",
        "You have {count} event credits. Each service day uses one.",
        credits || 0
      )
    : account_type === "business"
    ? tn(
        "You have {count} event credit. Each event day uses one.",
        "You have {count} event credits. Each event day uses one.",
        credits || 0
      )
    : tn(
        "You have {count} prepaid event – it will be used for this event.",
        "You have {count} prepaid events – one will be used for this event.",
        credits || 0
      );

  const onBackPressed = () => {
    if (form.current.isReadyToSave()) {
      showActionSheetWithOptions(
        {
          options: [t("Leave"), t("Keep editing")],
          cancelButtonIndex: 1,
          destructiveButtonIndex: 0,
          title: funeralMode
            ? t("Leave without scheduling the service?")
            : t("Leave without scheduling the event?"),
        },
        (buttonIndex) => {
          if (buttonIndex === 0) {
            onBack();
          }
        }
      );
    } else onBack();
  };

  const orgName = intent?.orgName;

  const title = fundraiserMode
    ? t("Schedule your live fundraiser")
    : funeralMode
    ? t("Schedule a service")
    : first
    ? t("Schedule your first event")
    : t("Schedule your event");
  const description = fundraiserMode
    ? orgName
      ? t(
          "Next, you'll confirm {organization} as the organization receiving donations. You can change the details later.",
          { organization: orgName }
        )
      : t(
          "Next, you'll choose the organization receiving donations. You can change the details later."
        )
    : funeralMode
    ? t(
        "The family gets one private link to watch live or later. You can change the details any time."
      )
    : t("You can change the details later.");

  return (
    <AppContainer>
      <AppHeader
        leftButtonComponent={
          <TouchableOpacity
            activeOpacity={1}
            onPress={onBackPressed}
            accessibilityRole="button"
            accessibilityLabel={t("Close")}
          >
            <Ionicons name="close-outline" size={30} color={colors.brand} />
          </TouchableOpacity>
        }
      />
      <KeyboardAvoidingView
        behavior="padding"
        style={{ flex: 1 }}
        keyboardVerticalOffset={-80}
      >
        <ScrollView
          contentContainerStyle={[
            tw`w-full self-center px-5 pt-2 pb-12`,
            { maxWidth: CONTENT_MAX_WIDTH + 40 },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <SettingsPage title={title} description={description}>
            {!!waiverMessage && (
              <Callout
                tone={waiverClaim?.status === "granted" ? "success" : "info"}
                style={tw`mb-4`}
              >
                {waiverMessage}
              </Callout>
            )}

            {!!credits && (
              <Callout tone="info" style={tw`mb-5`}>
                {creditsText}
              </Callout>
            )}

            <CreateEventForm
              ref={form}
              fundraiserMode={fundraiserMode}
              funeralMode={funeralMode}
              initialTitle={fundraiserMode ? fundraiserEventTitle(intent) : ""}
            />
          </SettingsPage>
        </ScrollView>
      </KeyboardAvoidingView>
    </AppContainer>
  );
};

export default CreateEvent;
